import { dataKeySchema } from "./integration-data-policy";

/** Parse a request declaration without a shell, network call, or retaining credential values. */
export function parseIntegrationRequest(input: string) {
  if (input.length > 32768 || /\$\(|`/.test(input))
    throw new Error(
      "Use a literal URL or cURL request without shell expressions.",
    );
  const tokens: string[] = [];
  let token = "",
    quote = "",
    started = false;
  for (let i = 0; i < input.length; i++) {
    const c = input[i];
    if (c === "\\" && quote !== "'" && i + 1 < input.length) {
      if (input[i + 1] === "\n") {
        i++;
        continue;
      }
      token += input[++i];
      started = true;
    } else if (quote) {
      if (c === quote) quote = "";
      else token += c;
    } else if (c === '"' || c === "'") {
      quote = c;
      started = true;
    } else if (/\s/.test(c)) {
      if (started) tokens.push(token);
      token = "";
      started = false;
    } else {
      token += c;
      started = true;
    }
  }
  if (quote) throw new Error("Close the quoted value in the cURL request.");
  if (started) tokens.push(token);
  if (/^curl(?:\.exe)?$/i.test(tokens[0] || "")) tokens.shift();
  let target = "",
    method = "",
    body = "",
    authHeader = "Authorization";
  const warnings: string[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const argument = tokens[i];
    const next = () => {
      if (tokens[i + 1] === undefined)
        throw new Error(`Missing value for ${argument}`);
      return tokens[++i];
    };
    if (["-X", "--request"].includes(argument)) method = next().toUpperCase();
    else if (["-H", "--header"].includes(argument)) {
      const name = next().split(":", 1)[0];
      if (/authorization|api[-_]?key|token/i.test(name)) {
        authHeader = name;
        warnings.push(
          "Credentials were removed. Configure the source credential separately on the server.",
        );
      } else if (!/^content-type$|^accept$/i.test(name))
        warnings.push(
          `Header ${name} was omitted; configure required headers in the host adapter.`,
        );
    } else if (["-d", "--data", "--data-raw"].includes(argument)) body = next();
    else if (argument === "--url") target = next();
    else if (["-G", "--get"].includes(argument)) method = "GET";
    else if (argument === "--silent" || argument === "-s") continue;
    else if (argument.startsWith("-"))
      throw new Error(
        `Request option ${argument} needs a host adapter; it was not imported.`,
      );
    else if (!target) target = argument;
    else throw new Error("Import one request at a time.");
  }
  const url = new URL(target);
  if (url.protocol !== "https:" || url.hash || (url.port && url.port !== "443"))
    throw new Error(
      "Use a public HTTPS endpoint without a fragment or custom port.",
    );
  if (url.username || url.password) {
    url.username = "";
    url.password = "";
    warnings.push(
      "URL credentials were removed. Configure credentials separately on the server.",
    );
  }
  const parameters: Record<string, string | number | boolean | null> =
    Object.create(null);
  const add = (key: string, value: unknown) => {
    dataKeySchema.parse(key);
    if (/password|secret|token|authorization|credential|api.?key/i.test(key)) {
      warnings.push(`Credential parameter ${key} was removed.`);
      return;
    }
    if (
      (value !== null &&
        !["string", "number", "boolean"].includes(typeof value)) ||
      (typeof value === "string" && value.length > 1000)
    )
      throw new Error(
        "Public parameters must be short text, numbers, booleans or null. Use a host adapter for nested request bodies.",
      );
    if (Object.hasOwn(parameters, key))
      throw new Error(`Parameter ${key} appears more than once.`);
    parameters[key] = value as string | number | boolean | null;
  };
  for (const [key, value] of url.searchParams) add(key, value);
  url.search = "";
  if (body) {
    const parsed = JSON.parse(body);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
      throw new Error("Use a JSON object for request data.");
    for (const [key, value] of Object.entries(parsed)) add(key, value);
  }
  const httpMethod = method || (body ? "POST" : "GET");
  if (!["GET", "POST"].includes(httpMethod))
    throw new Error(
      "Only read-only GET or JSON POST data queries are supported.",
    );
  if (Object.keys(parameters).length > 30)
    throw new Error("Use at most 30 public parameters.");
  return {
    endpoint: url.href,
    httpMethod: httpMethod as "GET" | "POST",
    requestStyle: "parameters" as const,
    parameterDefaults: parameters,
    parameterKeys: Object.keys(parameters),
    authHeader,
    warnings: [...new Set(warnings)],
  };
}
